import Vue from "vue";
import API from "app/axios";
import store from "./store";
import router from "./router";

/**
 *  Ascii
 *  https://patorjk.com/software/taag/#p=display&h=2&v=3&c=c%2B%2B&w=%20&f=ANSI%20Regular&t=Topic
 */

const userEngine = new Vue({
  router,
  store,

  data: function () {
    return {
      coretick: 1000 * 0.05, // tick interval (10th second)
      coresync: 1000 * 5, // sync interval (10s)
      coretime: null,
      trackOnce: {}, // Plausible event throttling..
      onboard_memo: JSON.parse(
        window.localStorage.getItem("onboard_memo") || "{}"
      ), // little list of things to remember at signup
      dialctrl: {
        pending: false,
        snap: null,
      }, // stuff for managing your dial progress through stacks etc
      bDebugChange: false,
      oStackUnlock: {
        on: false,
      },
      // https://nourished.news/api/2/data/event-dictionary
      eventDictionary: {
        "push-tap": { name: "Push: Tap" },
        "nourish-home-render": { name: "Nourish: Home Render" },
        "nourish-home-reset": { name: "Nourish: Home Reset" },
        "nourish-app-open": { name: "Nourish: App Open", roles: ["n2"] },
        "nourish-route": { name: "Nourish: Route", mode: "pageview" },
        "tap-tab": { name: "Tap: Tab" },
        "tap-nav": { name: "Tap: Nav" },
        "tap-action": { name: "Tap: Action:" },
        "tap-channel": { name: "Tap: Channel" },
        "tap-free": { name: "Tap: (Free)" },
        "tap-locked": { name: "Tap: (Locked)" },
        "tap-unlocked": { name: "Tap: (Unlocked)" },
        "tap-game": { name: "Tap: (Game)" },
        "tap-unlx-pro": { name: "Tap: Unlx - Pro" },
        "tap-unlx-invite": { name: "Tap: Unlx - Invite" },
        "tap-unlx-detox": { name: "Tap: Unlx - Detox" },
        "channel-watch": { name: "Channel: Watch" },
        "channel-rewatch": { name: "Channel: Rewatch" },
        "channel-video-sound": { name: "Channel: Video Audio" },
        "channel-preview-sound": { name: "Channel: Preview Audio" },
        "channel-read": { name: "Channel: Read" },
        "channel-read-outro": { name: "Channel: Read Outro" },
        "channel-deep-stx": { name: "Channel: Deep (Story)" },
        "channel-deep-arx": { name: "Channel: Deep (Article)" },
        "channel-close-x": { name: "Channel: Closed - X" },
        "channel-close-swipe": { name: "Channel: Closed - Swipe" },
        "channel-unlock": { name: "Channel: Unlock" },
        "channel-like": { name: "Channel: Like" },
        "channel-share": { name: "Channel: Share" },
        "tap-invite-copy": { name: "Tap: Invite Copy" },
        "tap-invite-share": { name: "Tap: Invite Share" },
        "headline-topic": { name: "Headlines: Topic" },
        "headline-story": { name: "Headlines: Story" },
        "ask-search": { name: "Ask: Search" },
        "ask-topic": { name: "Ask: Topic" },
        "ask-story": { name: "Ask: Story" },
        "newsle-share": { name: "Newsle: Share" }, 
        "newsle-win": { name: "Newsle: Win" }, 
        "newsle-loss": { name: "Newsle: Loss" }, 
        "feedback-general": { name: "Feedback: General" },
        "feedback-ask-rating": { name: "Feedback: Ask Rating" }, 
        "feedback-fewer-stars": { name: "Feedback: Fewer Stars" },
        "feedback-fewer-notes": { name: "Feedback: Fewer Notes" },
        "feedback-five-stars": { name: "Feedback: Five Stars" },
        "feedback-five-notes": { name: "Feedback: Five Notes" },

        // pre-login things with no spirit-side collection -------- DO NOT OVERWRITE FROM DATABASE! -----------
        "invite-continue": { name: "Onboard: Continue" },
        "invite-download": { name: "Onboard: Download" },
        "invite-copy": { name: "Onboard: Copy Invite" },
        "invite-share": { name: "Onboard: Social" },
      },
    };
  },

  computed: {
    get() {
      return this.$store.getters.getAccount || {};
    },

    getAge() {
      // returns age of account, in days
      return this.get.details?.created
        ? Math.floor(
            (new Date() - new Date(this.get.details?.created)) / (1000 * 86400)
          )
        : 0;
    },

    getTimers() {
      return this.$store.getters.timers;
    },

    token() {
      return this.$store.getters.getToken;
    },

    isLoggedIn() {
      return !!this.token;
    },

    isLoaded() {
      return this.getPref('_id');
    },

    hasApp() {
      return this.$store.getters.hasApp;
    },

    hasNotifications() {
      let aStates = ["authorised", "authorized"]; // irritating!
      let iOS = aStates.includes(
        this.$userEngine.getPref("onesub.devices.ios.apn.state")
      );
      let iAndroid = aStates.includes(
        this.$userEngine.getPref("onesub.devices.android.apn.state")
      );
      // EITHER is set
      return iOS || iAndroid;
    },

    hasInviteSlots() {
      return this.get.system?.invites?.slots;
    },

    hasInviteAccepted() {
      return this.get.system?.invites?.accepted;
    },

    isInviteFlow() {
      let bIsGuest = !this.$store.getters.isLoggedIn;
      let bIsToken = !!this.$store.getters.getToken; /// okay ... if there's a token, we're *LIKELY* logged in
      let bIsN2Already =
        this.$store.getters.isNourish2 || (this.hasApp && !this.isLoggedIn);
      let bIsInvited = this.getOnboardMemo()?.invited_by;
      // update: July 2025 -- allow some cases where we hit N2 early, even uninvited
      let bIsInviteFlow = !bIsToken && bIsGuest && (bIsInvited || bIsN2Already);
      // console.log(`\x1b[33mInvite Flow: `, bIsInviteFlow, {bIsGuest: bIsGuest, bIsInvited: bIsInvited, bIsN2Already: bIsN2Already});
      return bIsInviteFlow;
    },

    isNourishLegacy() {
      let bIsLoggedIn = this.$store.getters.isLoggedIn;
      let bIsToken = !!this.$store.getters.getToken;
      let bIsN2Already = this.$store.getters.isNourish2;
      let bIsLegacy = (bIsLoggedIn || bIsToken) && !bIsN2Already;
      // console.log(`\x1b[33mIs Legacy: ${bIsLegacy} = (${bIsLoggedIn} || ${bIsToken}) && !${bIsN2Already}`)
      return bIsLegacy;
    },

    // for the watchers
    visibleSlugs() {
      // until login, consider nothing visible... (timing)
      if (!this.token) {
        return [];
      }
      // need to include "pro" data
      let aWatch = [];

      // which editions are we monitoring?
      let aEditions = [
        // 'edition_v3', // do we need this anymore?
        "edition_v6",
        "edition_pro",
      ];

      // stack all the slugs..
      aEditions.forEach((sEdition) => {
        aWatch.unshift(
          ...(this.$store.getters
            .getLibraryItem(sEdition)
            ?.stories?.map((a) => a.slug) || [])
        );
      });

      // add any topic slugs (for /deck .. but for topic pages when they get tarted up too, presumably!)
      aWatch = aWatch.concat(this.$store.getters.getAllTopicSlugs || []);
      aWatch = aWatch.concat(this.$store.getters.getAllTopicArticleSlugs || []);

      // return the list of slugs we care about..
      return aWatch;
    },

    // wrap the user's preferences (do we need individual getters?)
    prefs() {
      return (
        this.$store.getters.getSettings || {
          build: "-",
          edition: {
            locale: "", // set by the API & locked in thereafter (actually, ignored by axios.js at the moment..)
          },
          layout: {
            next: "section",
            theme: "auto",
            feed: {
              cleanlog: {},
            },
          },
        }
      );
    },

    getStreak() {
      if (!this.dial?.streaks) {
        return 0;
      }
      return this.dial.streaks.soft;

      // return Math.max(this.dial.streaks.time, this.dial.streaks.topics, this.dial.streaks.locales);
    },

    hasStreakToday() {
      return this.dial?.streaks?.today?.soft || false;
    },

    /**
     *
     * --------------------------- DIAL & PROGRESS -------------------------------
     */

    dial() {
      return this.$store.getters.getLibraryItem("dial") || {};
    },

    dialTracks() {
      const tracks = [
        {
          slug: "time",
          icon: "onboarding-clock",
          thing: "minute",
          unlocked: false,
        },
        {
          slug: "topics",
          icon: "onboarding-book",
          thing: "subject",
          unlocked: false,
        },
        {
          slug: "locales",
          icon: "onboarding-globe",
          thing: "country",
          unlocked: false,
        },
      ];

      // Check which tracks are locked
      tracks.forEach((track) => {
        if (this.dialUnlocked.includes(track.slug)) {
          track.unlocked = true;
        }
      });

      // Sort tracks with unlocked at the top
      tracks.sort((a, b) => {
        if (a.unlocked === b.unlocked) return 0;
        return a.unlocked ? -1 : 1;
      });

      return tracks;
    },

    dialDiffReady() {
      return this.dial.rings && this.dialctrl.snap && !this.dialctrl.pending;
    },

    dialDiff() {
      if (!this.dialDiffReady) {
        this.notice(`[Dial] - (Not ready for diff)`);
        return {};
      }
      let oNow = this.dial.rings;
      let oSnap = this.dialctrl.snap;
      this.notice(`[Dial] - (Ready!)`, oNow?.time?.cx, oSnap?.time?.cx, oSnap);
      let oDiff = {};
      ["time", "topics", "locales"].forEach((s) => {
        oDiff[s] = {
          cx: oNow[s].cx - oSnap[s].cx,
          px: oNow[s].px - oSnap[s].px,
          tx: oNow[s].tx - oSnap[s].tx,
        };
      });
      return oDiff;
    },

    dialFocus() {
      return this.$coachingEngine.getDialFocus;
    },

    dialUnlocked() {
      return this.$coachingEngine.getDialActive;
    },

    getDialReckoner() {
      if (!this.dial?.rings) {
        return "";
      }

      let r = {
        time: this.dial?.rings?.["time"]?.px || 0,
        topics: this.dial?.rings?.["topics"]?.px || 0,
        locales: this.dial?.rings?.["locales"]?.px || 0,
      };

      // all done?
      if (r["time"] + r["topics"] + r["locales"] == 300) {
        return `<strong>Awesome!</strong> You're done for today.  Put your phone away.`;
      }

      // not started?
      if (r["time"] + r["topics"] + r["locales"] == 0) {
        return `<strong>Hi there.</strong> Let's get started.  Why not start with your Five a Day?`;
      }

      if (r["time"] + r["topics"] + r["locales"] <= 100) {
        return `<strong>Great start.</strong> You're doing great.  We hope you've found something interesting?`;
      }

      if (r["time"] + r["topics"] + r["locales"] >= 200) {
        return `<strong>Nearly there.</strong> You've made a great start.  Why not finish off with something a little different?`;
      }

      return `<strong>Well done…</strong> You're making great progress.  What are you going to read next?`;
    },

    isOnboardStarted() {
      return (
        this.getPref("nourish.onboarding.done") ||
        this.getPref("nourish.onboarding.focus")
      );
    },

    isOnboardComplete() {
      return this.getPref("nourish.onboarding.done");
    },

    /**
     *
     * --------------------------- STACK MANAGEMENT -------------------------------
     */

    isUnlockMode() {
      return this.oStackUnlock.on;
    },
  },

  methods: {
    instantiate() {
      // console.log(`[userEngine] -- User Loaded... (Is this ever called!?)`);
    },

    /**
     *
     * --------------------------- ONBOARDING MEMOS -------------------------------
     */

    setOnboardMemo(memo) {
      // console.log(`Adding: `, memo, this.onboard_memo);
      // overwrite the new stuff
      this.onboard_memo = {
        ...this.onboard_memo,
        ...memo,
      };
      // store in localstorage (just for odd sessions - more fair this way)
      window.localStorage.setItem(
        "onboard_memo",
        JSON.stringify(this.onboard_memo)
      );
      // hello!
      // console.log(`Onboard Memo:`, memo, this.onboard_memo);
    },

    getOnboardMemo() {
      // overwrite anything that's weirdly not in localstorage
      return {
        ...JSON.parse(window.localStorage.getItem("onboard_memo") || "{}"),
        ...this.onboard_memo,
      };
    },

    unsetOnboardMemo(key) {
      delete this.onboard_memo[key];
      // store in localstorage (just for odd sessions - more fair this way)
      window.localStorage.setItem(
        "onboard_memo",
        JSON.stringify(this.onboard_memo)
      );
    },

    clearOnboardMemo() {
      this.onboard_memo = {};
      window.localStorage.removeItem("onboard_memo");
    },

    ingestInvitedBy() {
      if (this.$route.query?.invite) {
        console.log(`Invite present: ${this.$route.query?.invite}`);
        if (!this.getOnboardMemo()?.invited_by) {
          console.log(
            `Invite clear so far.. Setting: ${this.$route.query?.invite}`
          );
          this.setOnboardMemo({
            invited_by: this.$route.query?.invite,
          });
        }
      }
    },

    checkN2OnboardFunnel() {
      if (!this.$store.getters.getAccount.token) {
        return;
      }
      if (!this.$store.getters.isNourish2) {
        return;
      }
      if (this.isOnboardStarted) {
        return;
      }
      // console.log(`WAIT,WHAT?`);
      // ... I think this was added to streamline the account-reset process ... but it gets in the way for normal users ..
      // ... probably because .token isn't alive for a few seconds ...
      // this.$router.push('/n/welcome/intro');
    },

    /**
     *
     * --------------------------- DIAL & PROGRESS -------------------------------
     */

    dialRefresh() {
      API.getDial({ detail: true });
      API.getDialHistory();
    },

    dialSnapshot() {
      if (!this.dial.rings) {
        this.dialctrl.pending = true; // flag for a watcher
        return;
      }
      this.dialctrl.pending = false;
      this.dialctrl.snap = JSON.parse(JSON.stringify(this.dial.rings)); // strip all reactivity
      this.notice(`[Dial] - Snap`, this.dialctrl.snap.time);

      // debug?
      if (this.bDebugChange) {
        Object.keys(this.dialctrl.snap).forEach((key) => {
          let iAdjustment = Math.floor(Math.random() * 25);
          this.dialctrl.snap[key]["cx"] = Math.max(
            0,
            this.dialctrl.snap[key]["cx"] - iAdjustment
          );
          this.dialctrl.snap[key]["px"] = Math.round(
            (this.dialctrl.snap[key]["cx"] / this.dialctrl.snap[key]["tx"]) *
              100
          );
        });
        this.notice(`[Dial] - Snap (Adjusted)`, this.dialctrl.snap.time);
      }
    },

    dialTrackItem(slug) {
      return this.dialTracks?.find((o) => o.slug == slug) || {};
    },
    dialTrackDiffItem(slug) {
      return this.dialDiff[slug] || {};
    },

    dialTrackBlock(slug) {
      let oData = this.dial?.rings?.[slug] || {};
      let oItem = this.dialTrackItem(slug);

      // eek - time is in seconds!
      // let iCX = (slug == 'time') ? Math.floor(oData.cx / 60) : oData.cx; // current
      // let iPX = (slug == 'time') ? Math.floor(oData.px / 60) : oData.px; // percent
      // let iTX = (slug == 'time') ? Math.floor(oData.tx / 60) : oData.tx; // target
      let iCX = oData.cx; // current
      let iPX = oData.px; // percent
      let iTX = oData.tx; // target

      return {
        cx: iCX,
        px: iPX,
        tx: iTX,
        rx: iTX - iCX,
        thing: oItem.thing,
      };
    },

    dialTrackLabel(slug) {
      let o = this.dialTrackBlock(slug);
      let sThing = this.$options.filters.plural(o.cx, o.thing);
      let unlocked = this.dialUnlocked.includes(slug);

      if (!unlocked) {
        const thing = this.$options.filters.plural(2, o.thing);
        return thing.charAt(0).toUpperCase() + thing.slice(1);
      }

      if (o.cx == 0) {
        return `-`;
      } else {
        return `${o.cx} ${sThing}`;
      }
    },

    dialTrackDiffLabel(slug) {
      let o = this.dialTrackBlock(slug);
      let oDiff = this.dialTrackDiffItem(slug);
      let sThing = this.$options.filters.plural(oDiff.cx, o.thing);
      // console.log(sThing, oDiff, o);
      if (oDiff.cx == 0) {
        return ``;
      }
      return `+${oDiff.cx} ${sThing}`;
    },

    dialTrackReckoner(slug) {
      let o = this.dialTrackBlock(slug);
      let sThing = this.$options.filters.plural(o.rx, o.thing);

      let unlocked = this.dialUnlocked.includes(slug);

      if (!unlocked) {
        return "Locked";
      }

      if (o.cx == 0) {
        return `-`;
      } else if (o.px >= 100) {
        return `You're done!`;
      }
      return `${o.rx} ${sThing} to go!`;
    },

    dialTrackUnlocked(slug) {
      if (this.dialUnlocked.includes(slug)) {
        return true;
      }
      return false;
    },

    /**
     *
     * --------------------------- STACK MANAGEMENT -------------------------------
     */

    stackUnlock(sMethod) {
      switch (sMethod) {
        case "on":
          this.oStackUnlock.on = true;
          break;
        case "off":
          this.oStackUnlock.on = false;
          break;
      }
    },

    /**
     *
     * --------------------------- ............. -------------------------------
     */

    accountChecks() {
      let user = this.$store.getters.getAccount;
      if (!user.token) {
        // not logged in
        return;
      }
      // console.log(`[userEngine] -- Account Loaded: `, user);

      // set the timezone (LEGACY)
      this.setPref(
        "edition.zone",
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );

      // used from Nourish onwards..
      this.setPref("timezone.offset", new Date().getTimezoneOffset());
      this.setPref(
        "timezone.region",
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );

      // check for onboarding questions?
      if (user.token == "ABC") {
        this.EventBus.$emit("coaching:show", {
          tab: `dashboard`,
          state: `personalise`,
          content: {
            dial: `content`, // - Default is false, to show large dial above, set to `full`. To show dial smaller and left of content, set to `content`
            heading: `Heading text`, // - Don`t include to hide heading.
            text: `Paragraph text`, // - Don`t include to hide paragraph.
            cta: {
              text: `Got it!`, // - Button text. Don`t include to hide button.
              action: {
                type: `close`, // - No action needed if it`s just to close, will close by default, otherwise pass `route` to navigate to another route and close coaching.
                route: ``, // - Set type to `route` and pass the route you want it to go.
                // Action designed like this to have some flexibiltiy later with different types of actions
              },
            },
          },
        });
      }
    },

    coachSync(state) {
      // max: once a day (or a reload?)
      let tOnce = this.getPrefTime("coaching.guest_story_swipe");
      if (tOnce && tOnce < 86400) {
        return;
      }
      // time the right state?
      if (state != "glanced") {
        return;
      }
      // okay - coach!
      this.setPref("coaching.guest_story_swipe", new Date().toUTCString());
      // this.EventBus.$emit("coaching:show", {
      //   tab: `dashboard`,
      //   content: {
      //     heading: `Track your reading habits`, // - Don`t include to hide heading.
      //     text: `Nourish helps you track what you read, so you can read a healthier news diet.`, // - Don`t include to hide paragraph.
      //     cta: {
      //       text: `Create my free Nourish account.`, // - Button text. Don`t include to hide button.
      //       action: {
      //         type: `event`,
      //         event: `modal:auth`,
      //       },
      //     },
      //   },
      // });
    },

    reload() {
      API.getAccount(this.$store.getters.getToken);
    },

    getPref(path) {
      return path
        ? path.split(".").reduce((a, c) => {
            return a ? a[c] : null;
          }, this.prefs)
        : this.prefs;
    },

    getPrefTime(path) {
      let v = this.getPref(path);
      if (!v) {
        return false;
      }
      return Math.round((new Date() - new Date(v)) / 1000);
    },

    setPref(path, newValue, bForceSave = false, bWrite = false) {
      let steps = path.split(".");
      let iterator = (i) => {
        if (i < steps.length) {
          // get the current value at this step..
          // *REALLY* important to clone the object here (otherwise we're playing with references!)
          let stepValue = {
            ...(this.getPref(steps.slice(0, i).join(".")) || {}),
          };
          // overwrite the key at this position with an iteration deeper
          stepValue[steps[i]] = iterator(i + 1);
          // and return..
          return stepValue;
        } else {
          // reached the end of the path -- overwrite what we have!
          return newValue;
        }
      };

      // start at the bottom & iterate!
      let newSettings = iterator(0);

      // ------------------------ COMMENT OUT AS ONE BLOCK! ---------------------------------

      // let sOldFingerprint = JSON.stringify(this.prefs);
      // let sNewFingerprint = JSON.stringify(newSettings);

      // console.log('Setings: (Old): ', sOldFingerprint);
      // console.log('Setings: (New): ', sNewFingerprint);

      // let bPossibleChange = (sOldFingerprint !== sNewFingerprint);

      // console.log(`Set: ${path} = ${newValue} :: (Change possible: ${bPossibleChange})`, newSettings);

      // ====================================================================================

      this.$store.commit("updateSettings", newSettings);

      // NORMALLY deep watcher will store, only when actually changed against the DB..
      // Deck doesn't always appear to work, somehow, through the watcher..
      if (bForceSave) {
        API.patchSettings(newSettings, bWrite);
      }

      // in case anything wants to re-think
      this.EventBus.$emit("user:prefs", {
        path: path,
        to: newValue,
      });

      // return
      return newSettings;
    },

    navChange(to, from) {
      this.notice(`Nav change:`, to, from);

      // Start/Stop timers
      this.notice(`[NavChange] : STOP  :`, from.params);
      this.navStopTimer(from.params);
      this.notice(`[NavChange] : START :`, to.params);
      this.navStartTimer(to.params);

      // hit the API for new slugs
      this.notice(`[NavChange] : SYNC  :`, [to.params.slug, to.params.node]);
      this.syncReadState([to.params.slug, to.params.node]);
    },

    syncReadState(aSlugs) {
      // filter
      let iStart = aSlugs.length;
      aSlugs = aSlugs.filter((s) => {
        // this.notice(`Slug: ${s}, State: `, !this.$store.getters.readState(s)?.slug, this.$store.getters.readState(s)?.slug);
        return !this.$store.getters.readState(s)?.slug;
      });
      aSlugs = aSlugs.filter((s) => s != "...");
      aSlugs = aSlugs.filter(Boolean);
      let iEnd = aSlugs.length;

      // check for both (means we can remove the debug below without compiler crying)
      if (!iStart || !iEnd) {
        return;
      }

      // redux?
      let iRedux = Math.abs(Math.round(((iEnd - iStart) / iStart) * 100));

      this.notice(`Syncing Read State (${iRedux}%) : `, aSlugs.length);
      API.getReadStateV3(aSlugs).then(({ data }) => {
        Object.values(data).forEach((a) => {
          this.$store.commit("setReadState", a);
        });
      });
    },

    readState(slug) {
      return this.$store.getters.readState(slug) || {};
    },

    /**
     *
     *  ------------------- Core Timing Mechanism -----------------------------
     */

    navStopTimer(p) {
      return this.navTimer("stop", p, 0);
    },
    navStartTimer(p) {
      return this.navTimer("start", p, 0);
    },

    navTimer(m, p, i) {
      // create a slug for this specific object...
      let sTimerSlug = [p.mode, p.slug, p.node].filter(Boolean).join(":");

      if (!sTimerSlug) {
        // this.notice(`${m} timer: '${sTimerSlug}' [No timer slug?]`, m, p);
        return;
      }

      if (!["story"].includes(p.mode)) {
        this.notice(`${m} timer: '${sTimerSlug}' [NOPE - NOT A STORY]`, m, p);
        return;
      }

      // if we've got this far and *don't* have a node (because we tidied the URL structure for SEO) then compensate!
      if (!p.node) {
        let oClusters = this.$dataEngine.clustersGet();
        // this.error(`${m} timer: '${sTimerSlug}' -- Waiting for node ID - compensating! `, {...p}, oClusters);
        if (!oClusters) {
          if (i > 25) {
            // this.error(`${m} timer: '${sTimerSlug}' -- Waiting for node ID - Giving up! `, {...p}, oClusters);
            return;
          }
          window.setTimeout(() => {
            // this.notice(`Restarting timer (iteration: ${i})`);
            this.navTimer("start", p, ++i);
          }, 250);
        }
        return;
      }

      // this.notice(`${m} timer: '${sTimerSlug}' `, m, p);

      // identify the timer & create if necessary
      if (!this.$store.getters.timer(sTimerSlug)) {
        // this.notice(`${m} created, ${sTimerSlug}`);
        this.$store.commit("timer", {
          slug: sTimerSlug,
          timer: {
            meta: p,
            seconds: 0,
            elapsed: 0,
            moments: 0,
            current: 0,
            running: false, // epoch of last running time
            periods: [], // tracking periods
            syncnxt: 0,
          },
        });
      }

      let timer = this.$store.getters.timer(sTimerSlug);

      if (m == "start") {
        timer.running = new Date().getTime();
        timer.syncnxt = timer.running;
      } else if (m == "stop") {
        // careful this doesn't happen twice (and add all of time() to the stack!)
        if (timer.running) {
          // current elapsed time
          let elapsed = new Date().getTime() - timer.running;
          // stack
          timer.periods.push(elapsed);
          // clear the timer
          timer.running = false;
          timer.syncnxt = false;
        }
      }

      this.$store.commit("timer", {
        slug: sTimerSlug,
        timer: timer,
      });

      // calcs..
      this.calcElapsed(sTimerSlug);

      // sync..
      this.sync(sTimerSlug, m);

      return this.$store.getters.timer(sTimerSlug);
    },

    // update a
    calcElapsed(sTimerSlug) {
      let timer = this.$store.getters.timer(sTimerSlug);

      timer.current = timer.running ? new Date().getTime() - timer.running : 0;

      // calculate total elapsed time.
      // work around blur/focus bug
      timer.elapsed =
        timer.periods.reduce((a, c) => {
          // ignore any periods > 3 minutes
          let iMaxMinsPerPeriod = 3; // minutes
          let iMaxMillisecondsPerPeriod = iMaxMinsPerPeriod * 60 * 1000; // milliseconds
          return a < iMaxMillisecondsPerPeriod ? a + c : c;
        }, 0) + timer.current;

      timer.moments = timer.periods.length;
      timer.seconds = (timer.elapsed / 1000).toFixed(2);

      this.$store.commit("timer", {
        slug: sTimerSlug,
        timer: timer,
      });

      // sync? (intervals only..)
      if (timer.running && new Date().getTime() > timer.syncnxt) {
        // sync:
        this.sync(sTimerSlug, "interval");
        // this.notice(`Update: ${sTimerSlug} (${timer.running}) -- ${timer.current} / ${timer.elapsed}`);
      }
    },

    /**
     *  ------------------- MAIN TICK! -----------------------------
     */

    tick() {
      // this.notice(this.$store.getters.timers_runnning_keys);
      this.$store.getters.timers_runnning_keys.forEach((slug) => {
        this.calcElapsed(slug);
      });
    },

    /**
     *  ------------------- Core Timing Mechanism -----------------------------
     */

    sync(sTimerSlug, mode) {
      // load the template
      let payload = this.getSyncPayloadTemplate();

      // load the timer data
      let timer = this.$store.getters.timer(sTimerSlug);

      // update the syncnxt
      let lag = new Date().getTime() - timer.syncnxt;
      timer.syncnxt = new Date().getTime() + this.coresync;
      let nxt = timer.syncnxt - new Date().getTime();
      this.$store.commit("timer", {
        slug: sTimerSlug,
        timer: timer,
      });

      // ffs, unused vars
      this.debug(`Sync (${mode}) (${lag}, ${nxt})`);

      // build the payload
      let iMaxMinutes = 1.5; // ie 90s

      // max 90s per asset (story/article)
      // need to deal with the concept of 'focus'
      payload.time = Math.min(
        60 * iMaxMinutes,
        Math.round(timer.elapsed / 1000)
      ); // ms ➜ s

      // don't hammer the server in the first 5 seconds
      // (October, SEO fiddling..)
      if (payload.time < 5) {
        return;
      }

      // and give up after 60s * 3m = 180s too -- no-one cares
      // NB: the time is capped to MAX(60 * iMaxMinutes) above .. so it's never > ...
      // ..: this does mean we never actually record 90s .. which isn't ideal.  Needs tidying.
      if (payload.time >= 60 * iMaxMinutes) {
        return;
      }

      payload.story = timer.meta.slug || "";

      // removed: april 2024, cos it's super misleading.  They've not *actually* read the node article, it's arbitrary..
      // payload.article = timer.meta.node || '';
      payload.article = "";

      // including the state (very assumed for now, might/could come from read-time :shrug:)
      payload.state = this.getSyncState(timer);

      // into which day do we correlate the data?
      payload.offset = new Date().getTimezoneOffset();

      this.notice(
        `Sync (${mode}) (Current: ${timer.current}) (${lag}/${nxt}): SEND : ${payload.state}, ${payload.time} `,
        payload
      );
      // convert to something that *looks* like the response object
      let oCmmtPayload = {
        unread: 0,
        slug: payload.story,
        type: "story",
        name: payload.state.charAt(0).toUpperCase() + payload.state.slice(1),
        code: payload.state,
        px: Math.min(1, Math.round((payload.time / 90) * 100) / 100),
        scope: payload.scope,
        platform: payload.platform,
        time: payload.time,
        date: new Date().toUTCString(),
        updated: false,
      };
      this.$store.commit("setReadState", oCmmtPayload);

      if (!this.$store.getters.isLoggedIn) {
        this.coachSync(payload.state);
      }

      // sync with HQ
      this.pushSync(payload);
    },

    pushSync(payload) {
      // if (payload) {
      //   return;
      // }

      // no anon for now..
      if (!this.$store.getters.isLoggedIn) {
        this.notice("Sync: (Not logged in)");
        return;
      }

      API.post("/track/article/read", payload)
        .then(({ data }) => {
          // this.notice(`Sync (${mode}) (Current: ${timer.current}) (${lag}/${nxt}): RECV : `, data?.status);

          // map in all the updated story/article statuses..
          if (data?.status) {
            Object.values(data?.status).forEach((a) => {
              // this.notice(`Sync (${mode}) (Current: ${timer.current}) (${lag}/${nxt}): CMMT : `, a.slug, a);
              this.$store.commit("setReadState", a);
            });
          }

          // update the dial
          if (data?.dial) {
            // console.log({...data?.dial?.rings?.time});
            this.$store.commit("updateDial", data?.dial);
          }

          // update points!?
          if (data?._xp) {
            this.EventBus.$emit("xp:api", data?._xp);
          }
        })
        .catch((error) => {
          this.error(error);
        });
    },

    /**
     * Both updated to handle arrays of slugs (for /deck) - end April 2024
     */

    stackRead(oOverlay) {
      let oPayload = this.getSyncPayloadTemplate();
      // okay, clear out..
      delete oPayload.story;
      delete oPayload.article;
      // and fill in..
      oPayload.state = "read";
      // add the bits we've been given..
      oPayload = {
        ...oPayload,
        ...oOverlay,
      };
      this.pushSync(oPayload);
    },

    markRead(mSlugs, template = {}) {
      // let oTimer = this.navStopTimer(this.$route.params);
      // console.log(`Timer:: `, oTimer);

      // a little legacy tidying
      template =
        typeof template == "string" && template.length
          ? { state: template }
          : template;
      template.state = template.state || "read";

      // force array structure (/deck commits whole columns now)
      let aSlugs = !Array.isArray(mSlugs) ? [mSlugs] : mSlugs;
      aSlugs.forEach((slug) => {
        let oCmmtPayload = {
          ...this.getSyncPayloadTemplate(template),
          unread: 0,
          slug: slug,
          type: "story",
          name:
            template.state.charAt(0).toUpperCase() +
            template.state.substring(1),
          code: template.state,
          px: 1,
          date: new Date().toUTCString(),
          updated: false,
        };
        this.$store.commit("setReadState", oCmmtPayload);
      });

      // sync with HQ
      this.pushSync({
        ...this.getSyncPayloadTemplate(template),
        story: aSlugs.join(","),
        state: template.state,
      });
    },

    markArticleRead(mSlugs, template = {}) {
      // a little legacy tidying
      template =
        typeof template == "string" && template.length
          ? { state: template }
          : template;
      template.state = template.state || "read";

      // force array structure (/deck commits whole columns now)
      let aSlugs = !Array.isArray(mSlugs) ? [mSlugs] : mSlugs;
      aSlugs.forEach((slug) => {
        let oCmmtPayload = {
          ...this.getSyncPayloadTemplate(template),
          unread: 0,
          slug: slug,
          type: "article",
          name:
            template.state.charAt(0).toUpperCase() +
            template.state.substring(1),
          code: template.state,
          px: 1,
          date: new Date().toUTCString(),
          updated: false,
        };
        this.$store.commit("setReadState", oCmmtPayload);
      });

      // sync with HQ
      this.pushSync({
        ...this.getSyncPayloadTemplate(template),
        article: aSlugs.join(","),
        state: template.state,
      });
    },

    getSyncState(timer) {
      // load article?
      // if (timer.meta.node) {
      //   let article = this.$store.getters.getArticle(timer.meta.node);
      //   this.notice('Article: ', article);
      // }

      let seconds = timer.elapsed / 1000;
      let state = "clicked";
      if (seconds > 30) {
        state = "read";
      } else if (seconds > 15) {
        state = "skimmed";
      } else if (seconds > 5) {
        state = "glanced";
      } else {
        state = "glanced";
      }

      return state;
    },

    getDevice() {
      if (this.$store.getters.hasApp) {
        return this.$store.getters.hasAppPlatform;
      } else {
        return this.$store.getters.hasMobile ? "mobile" : "desktop";
      }
    },

    getSyncPayloadTemplate(template = {}) {
      // platform, version, scope (browser) defaults:
      let platform = this.$store.getters.hasMobile ? "mobile" : "desktop";
      let version = "";
      let scope = "browser";

      // unless in app:
      if (this.$store.getters.hasApp) {
        // platform = navigator.vendor.match('/Google/') ? 'android' : 'apple';
        platform = this.$store.getters.hasAppPlatform || "unknown";
        version = this.$store.getters.hasAppVersion;
        scope = "app";
      }

      return {
        article: null,
        story: null,
        state: null,
        time: null,
        scope: scope,
        version: version,
        platform: platform,
        ...template,
      };
    },

    // legacy(payload) {
    //   // Only track opt-in, real users..
    //   if (!this.loggedIn) {
    //     // console.log('Tracking: Not logged in');
    //     return;
    //   }

    //   // time: optional
    //   payload.time = payload.time || 0;

    //   // story: is optional
    //   let story = (payload.story === undefined) ? '' :
    //     ( typeof payload.story === 'object' ) ? payload.story.slug : payload.story;

    //   /**
    //    * ----------- SEND -------
    //    */
    //   let api_payload = {
    //     article: payload.article.slug,
    //     story: story,
    //     state: payload.state,
    //     time: payload.time,
    //     scope: scope,
    //     version: version,
    //     platform: platform
    //   };
    //   // console.log("Tracking: ", api_payload);
    //   this.deviceLog("Tracking: ....");
    //   API.post('/track/article/read',api_payload).then().catch((error) => {
    //     // console.log(error);
    //   });
    // },

    xpAPI(payload) {
      // if (!this.$store.getters.isAlpha) {
      //   return;
      // }
      // xp received over the API (typical for now)
      this.EventBus.$emit("tab:xp", payload.tx);
      this.$store.dispatch("xpAdd", payload.tx);
    },

    trackEvent(payload) {
      // should this event be throttled to once per session? (default: yes)
      let bOnce = payload.bOnce !== false;

      // consider throttling to once per session
      if (bOnce && this.trackOnce[payload.label || payload.event]) {
        // console.log(`Plausible: Throttling: '${payload.label}'`);
        return;
      }
      // record that this event has been triggered at least once
      this.trackOnce[payload.label] = true;

      // modernized --
      /**
       *  Modernisation
       *
       *  - legacy payload: {label: (string), props: (object)}
       *
       *  - modern: {event: (string), props: (object - for Plausible), detail: (object - for Spirit)}
       */

      if (!payload.event) {
        // legacy:

        // track (or stack) to Plausible
        console.log(
          `\u001b[36mPlausible: Triggering vanilla event: '${payload.label}'`,
          payload.props
        );
        window.plausible(payload.label, { props: payload.props });
        return;
      }

      // modern:

      if (!this.eventDictionary[payload.event]) {
        console.warn(
          `Event '${payload.event}' not recognised.  Check dictionary is up-to-date.`
        );
        return;
      }

      let oLabel = this.eventDictionary[payload.event];



      // track to Spirit (only if we're authed)
      if (this.isLoggedIn) {
        API.post(`/track/event/${payload.event}`, payload.detail || {});
        // track to XCode
        // console.log(`[trackEvent] SPIRIT :: ${payload.event} - Payload: ` + JSON.stringify(payload.detail));
        this.EventBus.$emit("deviceLog", `[trackEvent] SPIRIT :: ${payload.event} - Payload: ` + JSON.stringify(payload.detail));
      }

      // track to Plausible
      if (oLabel) {
        if (oLabel.mode == "pageview") {
          let sTrackUrl =
            window.location.origin + (payload.props?.mode || "/unknown");
          // console.log(
          //   `\u001b[36mPlausible: Triggering pageview: '${oLabel.name}'`,
          //   sTrackUrl,
          //   payload
          // );
          window.plausible("pageview", { url: sTrackUrl });
          return;
        } else {
          if (oLabel.roles) {
            let sRole = "guest";
            if (this.isLoggedIn) {
              sRole = this.$store.getters.isNourish2 ? "n2" : "n1";
            }
            if (!oLabel.roles.includes(sRole)) {
              // console.log(
              //   `\u001b[36mPlausible: Ignoring (ROLE ${sRole} != ${oLabel.roles}): '${oLabel.name}'`,
              //   oLabel,
              //   payload
              // );
              return;
            }
          }
        } // end special conditions... roll on..
        // console.log(
        //   `\u001b[36mPlausible: Triggering event: '${oLabel.name}'`,
        //   oLabel,
        //   payload
        // );
        window.plausible(oLabel.name, { props: payload.props });
        // track to XCode
        this.EventBus.$emit("deviceLog", `[trackEvent] PLAUSIBLE :: ${oLabel.name} - Payload: ` + JSON.stringify(payload.props));
      }
    },

    deviceBoot() {
      let sPlatform = this.getDevice();
      this.notice(`[Boot] : ${sPlatform}`);

      // assume we're doing this for the first time..
      let bIncrement = false;

      // but if we've seen this device in 30 days, increment the recent-counter!
      let sLast = this.getPref(`devices.${sPlatform}.last`);
      if (sLast) {
        let iOffsetDays = Math.floor(
          (new Date() - new Date(sLast)) / (1000 * 86400)
        );
        let iOffsetMins = Math.floor(
          (new Date() - new Date(sLast)) / (1000 * 60)
        );
        // if session split is ≥ 30 mins and less than 30 days, count up..
        bIncrement = iOffsetDays <= 30 && iOffsetMins >= 30;
      }

      bIncrement = false;

      // now, reaffirm the `last` and count if there's another ping.
      this.setPref(`devices.${sPlatform}.last`, new Date());
      if (bIncrement) {
        let iCountRecent = this.getPref(`devices.${sPlatform}.recent`) || 0;
        this.setPref(`devices.${sPlatform}.recent`, iCountRecent + 1);
      } else {
        // Removed ... not sure why we're setting to 1?
        // // if we're not pinging a count increment, then reset to 1
        // this.setPref(`devices.${sPlatform}.recent`, 1);

        // if this is the first time, set to one .. otherwise, ignore I guess? (Soft/freqent reboot, not worth counting ... but why would we reset?)
        if (!this.getPref(`devices.${sPlatform}.recent`)) {
          this.setPref(`devices.${sPlatform}.recent`, 1);
        }
      }

      this.notice(
        `[Boot] : ${sPlatform} - Last: ${sLast} (Ping: ${bIncrement})`
      );

      if (this.getDevice() === "android") {
        try {
          if (typeof Android !== "undefined") {
            // eslint-disable-next-line
            Android["messagingTokenCallback"]();
          }
        } catch (err) {
          // console.log('Android: ', err);
        }
      }
    },



    // Generalised from Lander.vue
    startGuestOnboarding(sFallbackInvite = null) {
      // if they're legacy -- (and uninvited?) force the invited by
      if (this.isNourishLegacy) {
        if (!this.$userEngine.getOnboardMemo()?.invited_by) {
          this.$userEngine.setOnboardMemo({
            invited_by: 'onesub',
          });
        }
      }

      // Track the event!
      this.EventBus.$emit('track:event', {
        label: 'Lander: Start',
        props: {
          nominator: this.$userEngine.getOnboardMemo()?.invited_by || 'None',
        },
      });
      
      
      // if they've got a valid invite code -- great -- otherwise, NO!
      let sInvite = this.$userEngine.getOnboardMemo()?.invited_by || sFallbackInvite;
      if (sInvite) {
        this.EventBus.$emit('layer:new', {
          component: 'Invite',
          opts: {},
        });
        
        // this.$router.push(`/~${sInvite}/invite`);
        // this.$router.push(`/n/welcome/invite?n=invite&from=${sInvite}`); // doesn't fulfil stuff .. skips steps
        // this.$router.push('/n/welcome');
      } else {
        this.EventBus.$emit('modal:nourish-appflow');
      }
    },

    debug() {},

    notice() {
      // console.log(`\x1B[36mUserEngine --`, ...arguments);
    },

    error() {
      // console.log(`\x1b[31mUserEngine --`, ...arguments);
    },
  }, // end methods

  created() {
    this.notice("Instantiated & ticking");
    this.coretime = window.setInterval(this.tick, this.coretick);
    // build the template of stuff that doesn't change per-session..
    this.payload_template = this.getSyncPayloadTemplate();
    // listen for account events (for sync, particularly)
    this.EventBus.$on("api:account", () => {
      this.accountChecks();
      this.dialRefresh();
      this.tick();
    });
    this.EventBus.$on('api:settings"', () => {
      this.checkN2OnboardFunnel();
    });
    // Plausible event tracking
    this.EventBus.$on("track:event", this.trackEvent);
    // XP Triggers
    this.EventBus.$on("xp:api", this.xpAPI);
    // Boot Events
    this.EventBus.$on("device:boot", this.deviceBoot);
    // globalise the setter & getter for prefs (still have to be an alpha for this to write new prefs)
    window._osxSetPref = this.setPref;
    window._osxGetPref = this.getPref;
    // trigger the navChange ... if we're not navigating to here the watcher (just below) doesn't go off...
    // this.navChange(this.$route, this.$route);
  },

  watch: {
    $route(to, from) {
      // console.log(`\x1B[36m UserEngine -- $route change`, to, from);
      this.navChange(to, from);
    },
    isLoaded() { // to, from) {
      // console.log(`\x1B[36m UserEngine -- Loaded State Change:`, to, from);
      this.EventBus.$emit('home:reset', {
        reason: 'Loaded State Change', 
        soft: true, // soft! don't navigate
      }); 
    },
    dial() {
      if (this.dialctrl.pending) {
        this.dialSnapshot();
      }
    },
    visibleSlugs(to) {
      // console.log(`\x1B[36m UserEngine -- Visible Slugs Mutation:`, to);
      this.syncReadState(to);
    },
    prefs: {
      deep: true,
      handler: function (to, from) {
        // console.log("Settings change: ", {
        //   from: from,
        //   to: to,
        //   same: (to === from),
        //   similar: (to == from),
        //   json: (JSON.stringify(to) == JSON.stringify(from)),
        //   json_to: JSON.stringify(to),
        //   json_from: JSON.stringify(from),
        // });
        if (from.build == "-") {
          // console.log("Settings change: BUILD STOP (GUEST)");
          return; // guest..
        }
        if (JSON.stringify(to) == JSON.stringify(from)) {
          // console.log("Settings change: NO CHANGE");
          return; // exactly the same...
        }
        // commit to database
        // console.log("Settings change: ** PATCH **");
        API.patchSettings(to);
        // 
        this.EventBus.$emit('user:prefs');
      },
    },
  },
});

Object.defineProperties(Vue.prototype, {
  $userEngine: {
    get: function () {
      return userEngine;
    },
  },
});
